import React, { forwardRef, useMemo, useRef } from 'react';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import useAutocomplete, { AutocompleteFreeSoloValueMapping } from '@mui/material/useAutocomplete';
import c from 'classnames';
import isEqual from 'lodash/isEqual';

import { ArrowIosDownward, Close } from '@lyearn/icons';

import Box from '../../Box';
import LinkButton from '../../LinkButton';
import Popper from '../../Popper';
import Stack from '../../Stack';
import { renderGroupedOptions, renderNoOptions } from './renderers';
import { getAutocompleteClasses } from './styles';
import { BaseAutocompleteProps } from './types';

// Reference: https://github.com/mui-org/material-ui/blob/master/packages/mui-material/src/Autocomplete/Autocomplete.js
const BaseAutocomplete = <
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  props: BaseAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  ref: React.Ref<HTMLDivElement>,
) => {
  const {
    autoComplete,
    autoHighlight,
    autoSelect,
    blurOnSelect,
    ChipProps,
    classes: classesProp,
    className,
    clearIcon = <Close />,
    clearOnBlur,
    clearOnEscape,
    clearText = 'Clear',
    closeText = 'Close',
    componentsProps = {},
    defaultValue,
    disableClearable = false,
    disableCloseOnSelect,
    disabled = false,
    disabledItemsFocusable,
    disableListWrap,
    disablePortal = false,
    error,
    filterOptions,
    filterSelectedOptions,
    forcePopupIcon = 'auto',
    freeSolo = false,
    fullWidth,
    getLimitTagsText = (more) => `+${more}`,
    getOptionLabel = (option) => (option as any).label ?? option,
    groupBy,
    handleHomeEndKeys,
    id: idProp,
    includeInputInList,
    inputValue: inputValueProp,
    isOptionEqualToValue,
    limitTags = -1,
    ListboxComponent = 'ul',
    ListboxProps,
    loading = false,
    loadingText = 'Loading',
    multiple = false,
    noOptionsText = 'No options',
    onChange,
    onClose,
    onHighlightChange,
    onInputChange,
    onOpen,
    open,
    openOnFocus,
    openText = 'Open',
    options,
    PaperComponent = Paper,
    placeholder,
    PopperComponent = Popper,
    popupIcon = <ArrowIosDownward />,
    renderGroup,
    renderInput,
    renderOption = getOptionLabel,
    renderTags,
    selectOnFocus,
    size = 'medium',
    value: valueProp,
    ...rest
  } = props;

  const previousValueRef = useRef(valueProp);
  // useAutocomplete resets the searchText when reference of value changes
  // so sending the previous reference if the value is not changed
  const autocompleteValue = useMemo(() => {
    if (!isEqual(previousValueRef.current, valueProp)) {
      previousValueRef.current = valueProp;
    }
    return previousValueRef.current;
  }, [valueProp]);

  const {
    anchorEl,
    dirty,
    focused,
    getClearProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    getPopupIndicatorProps,
    getRootProps,
    getTagProps,
    groupedOptions,
    id,
    inputValue,
    popupOpen,
    setAnchorEl,
    value,
  } = useAutocomplete<T, Multiple, DisableClearable, FreeSolo>({
    ...props,
    getOptionLabel: getOptionLabel as
      | ((option: AutocompleteFreeSoloValueMapping<FreeSolo> | T) => string)
      | undefined,
    value: autocompleteValue,
  });

  const hasClearIcon = !disableClearable && !disabled && dirty;
  const hasPopupIcon = (!freeSolo || forcePopupIcon === true) && forcePopupIcon !== false;
  const classes = useMemo(() => getAutocompleteClasses(size, classesProp), [classesProp, size]);

  const getInputStartAdornment = () => {
    let startAdornment;

    if (multiple && Array.isArray(value) && value.length > 0) {
      const getAdaptedTagProps = (params: { index: number }) => ({
        className: classes.tag!,
        disabled,
        ...getTagProps(params),
      });

      if (renderTags) {
        startAdornment = renderTags(value, getAdaptedTagProps);
      } else {
        startAdornment = value.map((option, index) => (
          <Chip label={getOptionLabel(option)} {...getAdaptedTagProps({ index })} {...ChipProps} />
        ));
      }
    }

    if (limitTags > -1 && Array.isArray(startAdornment)) {
      const more = startAdornment.length - limitTags;

      if (!focused && more > 0) {
        startAdornment = startAdornment.splice(0, limitTags);
        startAdornment.push(
          <Box key={startAdornment.length} className={classes?.tag} component="span">
            {getLimitTagsText(more)}
          </Box>,
        );
      }
    }

    return startAdornment;
  };

  const getInputEndAdornment = () => {
    return (
      <Stack className={classes?.endAdornment} gapX={2}>
        {hasClearIcon ? (
          <LinkButton
            aria-label={clearText}
            title={clearText}
            {...(getClearProps() as any)}
            {...componentsProps?.clearIndicator}
            className={c(classes?.clearIndicator, componentsProps?.clearIndicator?.className)}>
            {clearIcon}
          </LinkButton>
        ) : null}
        {hasPopupIcon ? (
          <LinkButton
            {...(getPopupIndicatorProps() as any)}
            aria-label={popupOpen ? closeText : openText}
            className={c(classes?.popupIndicator, popupOpen && classes?.popupIndicatorOpen)}
            disabled={disabled}>
            {popupIcon}
          </LinkButton>
        ) : null}
      </Stack>
    );
  };

  const showLoading = loading && groupedOptions.length === 0;
  const showNoOptions = groupedOptions.length === 0 && !freeSolo && !loading;

  return (
    <>
      <Box
        ref={ref}
        className={c(
          classes?.root,
          hasClearIcon && classes.hasClearIcon,
          hasPopupIcon && classes.hasPopupIcon,
          className,
        )}
        // to spread rest props on the root element
        {...(getRootProps as any)(rest)}>
        {renderInput({
          className: classes?.inputRoot,
          disabled,
          endAdornment: getInputEndAdornment(),
          error,
          fullWidth: true,
          id,
          inputProps: { className: classes?.input, disabled, ...getInputProps() },
          placeholder,
          // @ts-ignore
          ref: setAnchorEl,
          size,
          startAdornment: getInputStartAdornment(),
        })}
      </Box>
      {popupOpen && anchorEl ? (
        <PopperComponent
          open
          anchorEl={anchorEl}
          className={classes?.popper}
          disablePortal={disablePortal}
          role="presentation"
          style={{ width: anchorEl ? anchorEl.clientWidth : 0 }}>
          <PaperComponent className={classes?.paper}>
            {showLoading ? <Box className={classes?.loading}>{loadingText}</Box> : null}
            {showNoOptions ? renderNoOptions({ classes, noOptionsText }) : null}
            {groupedOptions.length > 0 ? (
              // @ts-ignore
              <ListboxComponent
                className={classes?.listbox}
                {...ListboxProps}
                {...getListboxProps()}>
                {renderGroupedOptions<T>({
                  classes,
                  getOptionProps,
                  groupBy,
                  groupedOptions,
                  inputValue,
                  renderGroup,
                  renderOption,
                })}
              </ListboxComponent>
            ) : null}
          </PaperComponent>
        </PopperComponent>
      ) : null}
    </>
  );
};

export default forwardRef(BaseAutocomplete) as typeof BaseAutocomplete;
